import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { cloneDeep } from 'lodash'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { POWER_DIALER_NAME_LIMIT } from 'shared/constants/limits'
import { PowerDialerApi } from 'entities/PowerDialer/api/powerDialer'
import { type IParamsPowerDialer, PowerDialerStatus } from 'entities/PowerDialer/api/types'
import { PowerDialer } from 'entities/PowerDialer/model/PowerDialer'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type IContactsDataParams } from 'entities/Contacts/api/types'
import type { EntitiesStore } from 'entities/EntitiesExport'
import { type IParamsFiltersListGroups } from 'entities/Broadcast/api/types'
import {
  POWER_DIALER_EMPTY_NAME_ERROR,
  TRIAL_CONTACTS_LIMIT,
  TRIAL_CONTACTS_LIMIT_LABEL,
} from 'widgets/PowerDialer/lib/constants'
import { getToastConfigByPowerDialerStatus } from 'widgets/PowerDialer/lib/getToastConfigByPowerDialerStatus'
import { type EnrollmentFilters } from 'widgets/ContactEnrollment'
import {
  type IPowerDialerEditorOrigin,
  PowerDialerStateStore,
} from 'widgets/PowerDialer/ui/PowerDialerEditor/store/powerDialerStateStore'
import { PowerDialerEditorModal } from 'widgets/PowerDialer/ui/PowerDialerEditor/ui/PowerDialerEditorModal/PowerDialerEditorModal'
import type { CallPopUpGlobalStore } from 'widgets/CallPopUp'
import { PowerDialerContactsStore } from './powerDialerContactsStore'

export class PowerDialerEditorStore {
  private readonly _contactsStore: PowerDialerContactsStore
  private readonly _stateStore: PowerDialerStateStore
  private readonly _editorModalId = nanoid()

  constructor(
    private _callPopUpGlobalStore: CallPopUpGlobalStore,
    private _entities: EntitiesStore
  ) {
    this._contactsStore = new PowerDialerContactsStore(this._entities)
    this._stateStore = new PowerDialerStateStore(this._entities)

    makeAutoObservable(this)
  }

  private _isLimitExceeded = (total: number) => {
    if (this._entities.subscriptionStore.isTrial && total > TRIAL_CONTACTS_LIMIT) {
      showToast({ title: TRIAL_CONTACTS_LIMIT_LABEL, type: 'error' })
      return true
    }
    return false
  }

  private _handlePowerDialerEditorAction = async (
    status: PowerDialerStatus.Draft | PowerDialerStatus.InProgress
  ) => {
    if (!this._stateStore.name) {
      this._stateStore.setNameError(POWER_DIALER_EMPTY_NAME_ERROR)
      return
    }

    const team = this._stateStore.teamId
      ? this._entities.inboxesStore.getItem(this._stateStore.teamId)
      : null
    if (!team || !(team instanceof Inbox) || !this._stateStore.callFrom) {
      this._stateStore.sendFromStore.setError(true)
      return
    }

    this._entities.eventLogStore.logEvent(
      'Power Dialer Used',
      {
        event_id: 'power_dialer_used',
        action: `created in ${this._stateStore.origin ?? 'unknown'}`,
      },
      { groupId: this._entities.organizationStore.id }
    )

    runInAction(() => {
      status === PowerDialerStatus.Draft
        ? this._stateStore.setDraftLoading(true)
        : this._stateStore.setCallLoading(true)
    })

    try {
      const filter: IParamsFiltersListGroups = cloneDeep(this._contactsStore.contactsFiltersParams)

      // Use number is_known filter as all selected w/o filters
      if (
        Object.keys(filter.filtersListGroups).length === 1 &&
        filter.filtersListGroups.advanced?.length === 1 &&
        filter.filtersListGroups.advanced[0].filtersList.length === 0
      ) {
        filter.filtersListGroups.advanced = [
          {
            filtersList: [
              {
                filters: [
                  {
                    key: 'number',
                    operator: 'is_known',
                    value: [],
                  },
                ],
                custom_filters: [],
                filter_tags: [],
              },
            ],
          },
        ]
      }

      const params: IParamsPowerDialer = {
        name: this._stateStore.name,
        filter,
        status,
        send_from: this._stateStore.callFrom,
      }

      const {
        data: { data: powerDialerResponse },
      } =
        this._stateStore.isEditMode && this._stateStore.id
          ? await PowerDialerApi.updatePowerDialer(this._stateStore.id, params)
          : await PowerDialerApi.createPowerDialer(params)

      const powerDialer = new PowerDialer(powerDialerResponse, this._entities)

      if (status === PowerDialerStatus.InProgress) {
        await this._callPopUpGlobalStore.openPowerDialer(powerDialer.id)
      } else if (!this._stateStore.isEditMode) {
        showToast(getToastConfigByPowerDialerStatus(status))
      }

      this._stateStore.closeEditorModal()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._stateStore.setDraftLoading(false)
        this._stateStore.setCallLoading(false)
      })
    }
  }

  handleSaveDraft = () => this._handlePowerDialerEditorAction(PowerDialerStatus.Draft)
  handleCall = () => this._handlePowerDialerEditorAction(PowerDialerStatus.InProgress)
  handleCancel = () => {
    this._stateStore.closeEditorModal()
    this.reset()
  }

  showCloseConfirmation = () => {
    const confirmModalId = nanoid()

    modalStore.addModal({
      id: confirmModalId,
      type: ModalTypeList.WARNING,
      title: 'Save Power Dialer campaign as a draft before closing?',
      disabledOverlayClose: true,
      disabledOnEscClose: true,
      onClose: () => {
        this.handleCancel()
        modalStore.removeModal(confirmModalId)
      },
      primaryAction: {
        text: 'Save as draft',
        onAction: () => {
          modalStore.removeModal(confirmModalId)
          void this._handlePowerDialerEditorAction(PowerDialerStatus.Draft)
        },
      },
      additionalSecondaryAction: {
        text: "Don't save",
        onAction: () => {
          modalStore.removeModal(confirmModalId)
          this.handleCancel()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.removeModal(confirmModalId)
        },
      },
    })
  }

  handleStartPowerDialerEditorFromContacts = async (
    contactsData: IContactsDataParams,
    origin: IPowerDialerEditorOrigin
  ) => {
    const { total = 0, contactsParams, selectedIds, bulkAll } = contactsData
    const count = bulkAll ? total : selectedIds.length
    if (this._isLimitExceeded(count)) return

    this._stateStore.setLoading(true)
    try {
      const contacts = bulkAll
        ? await this._contactsStore.startPagination({
            ...contactsParams,
          })
        : await this._contactsStore.startPagination({
            filtersListGroups: { contacts: selectedIds },
          })

      this._stateStore.setType('fromContacts')
      this._stateStore.setOrigin(origin)
      this._contactsStore.setSelectedContacts(contacts)
      this._stateStore.setDefaultInbox()
      this._openEditorModal()
    } catch (e) {
      logger.error(e)
    } finally {
      this._stateStore.setLoading(false)
    }
  }

  handleStartPowerDialerEditorFromFilters = (origin: IPowerDialerEditorOrigin) => {
    this._stateStore.setType('fromFilters')
    this._stateStore.setOrigin(origin)
    if (!this._stateStore.isEditMode) this._stateStore.setDefaultInbox()

    const filters = this._stateStore.enrollmentFilters.clone()

    const cancelFilters = () => Promise.resolve(true)
    const confirmFilters = (newFilters: EnrollmentFilters, total: number, skipped: number) =>
      this.confirmFiltersEnrollment(total, skipped, newFilters)

    this._stateStore.enrollmentStore.filterEditorStore.initFilters()
    this._stateStore.enrollmentStore.openModal({
      filters,
      action: { cancel: cancelFilters, confirm: confirmFilters },
      title: this._stateStore.titleModal.title,
      titleDividerContent: this._stateStore.titleModal.titleDividerContent,
    })
  }

  confirmFiltersEnrollment = (total: number, skipped: number, newFilters: EnrollmentFilters) => {
    return new Promise<boolean>(async (resolve) => {
      this._stateStore.enrollmentStore.setLoading(true)
      this._stateStore.setEnrollmentFilters(newFilters)

      if (this._isLimitExceeded(total + skipped)) {
        this._stateStore.enrollmentStore.setLoading(false)
        return resolve(false)
      }

      try {
        const contacts = await this._contactsStore.startPagination({
          filtersListGroups: this._stateStore.paramsFilters.filtersListGroups,
        })
        runInAction(() => {
          this._stateStore.setStep(2)
        })
        this._contactsStore.setSelectedContacts(contacts)
        this._openEditorModal()
        resolve(true)
      } catch (e) {
        logger.error(e)
        resolve(false)
      } finally {
        this._stateStore.enrollmentStore.setLoading(false)
      }
    })
  }

  private _openEditorModal = () => {
    this._stateStore.setModalId(this._editorModalId)

    modalStore.addModal({
      id: this._editorModalId,
      type: ModalTypeList.DEFAULT,
      title: this.stateStore.titleModal.title,
      titleDividerContent: this.stateStore.titleModal.titleDividerContent,
      width: 560,
      showCloseButton: false,
      paddingContent: '0px 24px 24px',
      ModalContent: PowerDialerEditorModal,
      ModalContentProps: { powerDialerStore: this },
      onClose: this.showCloseConfirmation,
      onOverlayClose: this.showCloseConfirmation,
    })
  }

  handleEditPowerDialer = async (powerDialer: PowerDialer) => {
    try {
      this._stateStore.setMode('edit')
      this._stateStore.setType('fromFilters')
      this._stateStore.setOrigin('pdCampaigns')

      const enrollmentParams = await this._stateStore.getEnrollmentFiltersParams(powerDialer.filter)

      this._stateStore.setId(powerDialer.id)
      this._stateStore.setName(powerDialer.name)
      this._stateStore.enrollmentFilters.sync({ ...enrollmentParams })

      const inboxId = powerDialer.number?.numberable?.id
      const numberId = powerDialer.number?.id

      if (inboxId && numberId) {
        this._stateStore.sendFromStore.setSelectItem({
          inbox_id: inboxId,
          number_id: numberId,
        })
      } else if (powerDialer.sendFrom) {
        this._stateStore.sendFromStore.setSendFrom(powerDialer.sendFrom)
      }

      this.handleStartPowerDialerEditorFromFilters('pdCampaigns')
    } catch (e) {
      logger.error(e)
    }
  }

  handleDuplicatePowerDialer = async (powerDialer: PowerDialer) => {
    try {
      this._stateStore.setMode('duplicate')
      this._stateStore.setType('fromContacts')
      this._stateStore.setOrigin('pdCampaigns')

      const contacts = await this._contactsStore.startPagination({
        filtersListGroups: powerDialer.filter || {},
      })

      const name = `Copy of ${powerDialer.name}`.slice(0, POWER_DIALER_NAME_LIMIT)
      const enrollmentParams = await this._stateStore.getEnrollmentFiltersParams(powerDialer.filter)

      this._stateStore.setName(name)
      this._contactsStore.setSelectedContacts(contacts)
      this._stateStore.enrollmentFilters.sync({ ...enrollmentParams })

      const inboxId = powerDialer.number?.numberable?.id
      const numberId = powerDialer.number?.id

      if (inboxId && numberId) {
        this._stateStore.sendFromStore.setSelectItem({
          inbox_id: inboxId,
          number_id: numberId,
        })
      } else if (powerDialer.sendFrom) {
        this._stateStore.sendFromStore.setSendFrom(powerDialer.sendFrom)
      }

      this._openEditorModal()
    } catch (e) {
      logger.error(e)
    }
  }

  handleRefreshContacts = async (powerDialer: PowerDialer, origin?: string) => {
    try {
      const {
        data: { data: powerDialerResponse },
      } = await PowerDialerApi.getPowerDialerWithUpdatedContactsCount(powerDialer.id)
      powerDialer.syncStatistic(powerDialerResponse)
      showToast({ type: 'info', title: 'Power Dialer contacts refreshed' })
      this._entities.eventLogStore.logEvent(
        'Power Dialer Used',
        {
          event_id: 'power_dialer_used',
          action: `refreshed in ${origin}`,
        },
        { groupId: this._entities.organizationStore.id }
      )
      return powerDialer
    } catch (e) {
      logger.error(e)
    }
  }

  loadNextContactsPage = () => this._contactsStore.loadNextContactsPage()
  deleteSelectedId = (id: number) => this._contactsStore.deleteSelectedId(id)
  onChangeShowAlert = (value: boolean) => this._stateStore.onChangeShowAlert(value)

  get contactsStore() {
    return this._contactsStore
  }

  get stateStore() {
    return this._stateStore
  }

  get editorActionButtonDisabled() {
    return !this._contactsStore.contactsCount || this._stateStore.isNumberNotVerifiedCallerId
  }

  reset = () => {
    this._contactsStore.reset()
    this._stateStore.reset()
  }
}
